import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { finShare, snapshot } from '@fcom/rx';
import { Profile } from '@fcom/core-api/login';
import { SelectOption } from '@fcom/common/interfaces';
import { AnalyticsFormStatus, FormStatus, softTicketNumberValidator, trackFormState } from '@fcom/service-forms';
import { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { LATIN_ADDRESS_PATTERN, LATIN_ALPHA_PATTERN, PNR_PATTERN, unsubscribe } from '@fcom/core/utils';
import { ButtonIcon, ButtonTheme, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { WindowRef } from '@fcom/core/providers';
import { GtmService } from '@fcom/common/gtm';
import {
  VatContactDetailsForm,
  VatPnrForm,
  VatReceiptContactDetails,
  VatReceiptSearch,
} from '@fcom/common/interfaces/corporate/vat-receipt.interface';
import { CorporateService, ZIP_CODE_PATTERN } from '@fcom/common/services/corporate/corporate.service';

import { atLeastOneRequired } from '../utils';

const CORPORATE_VAT_FORM_NAME = 'corporate-vat-form';

@Component({
  selector: 'fin-vat-receipt-form',
  templateUrl: './vat-receipt-form.component.html',
})
export class VatReceiptFormComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly FormStatus = FormStatus;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  pnrForm: FormGroup<VatPnrForm>;
  contactDetailsForm: FormGroup<VatContactDetailsForm>;
  formStatus: FormStatus;
  loadingIcon: ButtonIcon;

  countries$: Observable<SelectOption[]>;

  private subscriptions = new Subscription();
  profile$: Observable<Profile | undefined>;

  validatedPnrData$: Subject<VatReceiptSearch | null> = new BehaviorSubject(null);
  pnrNotFound$ = new BehaviorSubject(false);
  showCompanyDetailsForm$ = new BehaviorSubject(true);

  constructor(
    private windowRef: WindowRef,
    private elementRef: ElementRef,
    private fb: FormBuilder,
    private store$: Store<AppState>,
    private corporateService: CorporateService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.countries$ = this.corporateService.getCountryInputOptions();
    this.profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), take(1), finShare());
    this.formStatus = FormStatus.INITIAL;
    this.createForms();
    this.prefillForms();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  createForms(): void {
    this.pnrForm = this.fb.group(
      {
        lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
        bookingRef: ['', [Validators.pattern(PNR_PATTERN)]],
        eTicket: ['', [softTicketNumberValidator()]],
      },
      { validators: atLeastOneRequired(['bookingRef', 'eTicket']) }
    );
    this.contactDetailsForm = this.fb.group({
      addCompanyDetails: [false],
      companyName: ['', Validators.required],
      businessId: ['', Validators.required],
      address: ['', [Validators.required, Validators.pattern(LATIN_ADDRESS_PATTERN)]],
      postalCode: ['', [Validators.required, Validators.pattern(ZIP_CODE_PATTERN)]],
      city: ['', [Validators.required, Validators.pattern(LATIN_ALPHA_PATTERN)]],
      country: ['', Validators.required],
    });
    this.addControlListeners();
  }

  prefillForms(): void {
    this.subscriptions.add(
      this.profile$.subscribe((profile) => {
        if (profile) {
          this.pnrForm.get('lastName').setValue(profile.lastname);
          this.contactDetailsForm.get('companyName').setValue(profile.corporate?.name);
          this.contactDetailsForm.get('businessId').setValue(profile.corporate?.businessId);
          this.contactDetailsForm.get('address').setValue(profile.corporate?.street);
          this.contactDetailsForm.get('postalCode').setValue(profile.corporate?.zipcode);
          this.contactDetailsForm.get('city').setValue(profile.corporate?.city);
          this.contactDetailsForm.get('country').setValue(profile.corporate?.country);
          // Mark filled fields as touched
          const controls = Object.values(this.contactDetailsForm.controls);
          controls.forEach((control: AbstractControl) => control.value && control.markAsTouched());
        }
        // Show form if some required fields are missing
        this.showCompanyDetailsForm$.next(this.contactDetailsForm.invalid);
      })
    );
  }

  checkPnr(): void {
    if (this.pnrForm.invalid) {
      this.validatePnrForm();
      return;
    }
    this.setLoading(true);
    trackFormState(CORPORATE_VAT_FORM_NAME, `phase1-${AnalyticsFormStatus.SEND_ATTEMPT}`, this.gtmService);
    this.formStatus = FormStatus.LOADING;
    this.pnrNotFound$.next(false);
    this.subscriptions.add(
      this.corporateService
        .getVatReceipt({
          lastName: this.pnrForm.get('lastName').value,
          pnr: this.pnrForm.get('bookingRef').value,
          ticketNumber: this.pnrForm.get('eTicket').value,
        })
        .pipe(
          take(1),
          catchError((err: unknown) => {
            this.setLoading(false);
            trackFormState(CORPORATE_VAT_FORM_NAME, `phase1-${AnalyticsFormStatus.ERROR}`, this.gtmService);
            if ((err as HttpErrorResponse).status === 404) {
              this.pnrNotFound$.next(true);
            } else {
              this.formStatus = FormStatus.ERROR;
            }
            throw err;
          })
        )
        .subscribe((result) => {
          this.setLoading(false);
          this.formStatus = FormStatus.INITIAL;
          if (result.Booking) {
            trackFormState(CORPORATE_VAT_FORM_NAME, `phase1-${AnalyticsFormStatus.SUCCESS}`, this.gtmService);
            this.validatedPnrData$.next({
              lastName: this.pnrForm.get('lastName').value,
              pnr: this.pnrForm.get('bookingRef').value,
              ticketNumber: this.pnrForm.get('eTicket').value,
            });
          } else {
            trackFormState(CORPORATE_VAT_FORM_NAME, `phase1-${AnalyticsFormStatus.ERROR}`, this.gtmService);
            this.pnrNotFound$.next(true);
          }
        })
    );
  }

  downloadReceipt(): void {
    if (this.contactDetailsForm.get('addCompanyDetails').value && this.contactDetailsForm.invalid) {
      this.corporateService.scrollToInvalidField(this.contactDetailsForm, this.elementRef);
      return;
    }
    trackFormState(CORPORATE_VAT_FORM_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);
    this.setLoading(true);
    this.subscriptions.add(
      this.getPayload()
        .pipe(
          switchMap(({ search, contactDetails }) => {
            return this.corporateService.getReceiptUrl(search, contactDetails).pipe(
              take(1),
              catchError((err: unknown) => {
                this.setLoading(false);
                this.formStatus = FormStatus.ERROR;
                trackFormState(CORPORATE_VAT_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
                return throwError(() => err);
              })
            );
          })
        )
        .subscribe((url) => {
          this.setLoading(false);
          this.formStatus = FormStatus.COMPLETE;
          trackFormState(CORPORATE_VAT_FORM_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
          this.windowRef.nativeWindow.open(url, '_blank');
        })
    );
  }

  startOver(): void {
    this.validatedPnrData$.next(null);
    this.pnrForm.get('bookingRef').setValue(null);
    this.pnrForm.get('eTicket').setValue(null);
  }

  getPayload(): Observable<{ search: VatReceiptSearch; contactDetails: VatReceiptContactDetails }> {
    return this.showCompanyDetailsForm$.pipe(
      map((showForm) => {
        return {
          search: {
            lastName: this.pnrForm.get('lastName').value,
            pnr: this.pnrForm.get('bookingRef').value,
            ticketNumber: this.pnrForm.get('eTicket').value,
          },
          contactDetails: this.getContactDetailsForPayload(showForm),
        };
      })
    );
  }

  private getContactDetailsForPayload(showForm: boolean): VatReceiptContactDetails {
    // Return empty company details if user has left the "Add company details to receipt" checkbox unchecked.
    if (showForm && !this.contactDetailsForm.get('addCompanyDetails').value) {
      return {};
    }
    const countries = snapshot(this.countries$);
    const selectedCountry = countries.find((c) => c.value === this.contactDetailsForm.get('country').value);
    return {
      companyName: this.contactDetailsForm.get('companyName').value,
      companyBusinessId: this.contactDetailsForm.get('businessId').value,
      companyAddress: this.contactDetailsForm.get('address').value,
      companyPostalCode: this.contactDetailsForm.get('postalCode').value,
      companyCity: this.contactDetailsForm.get('city').value,
      companyCountry: selectedCountry.name,
    };
  }

  private validatePnrForm() {
    // If neither of the pnr or ticket number field is filled, show required error on both of those fields
    if (!this.pnrForm.get('bookingRef').value && !this.pnrForm.get('eTicket').value) {
      this.pnrForm.get('bookingRef').setErrors({ required: true });
      this.pnrForm.get('eTicket').setErrors({ required: true });
    }
    this.corporateService.scrollToInvalidField(this.pnrForm, this.elementRef);
  }

  private setLoading(loading: boolean) {
    this.formStatus = loading ? FormStatus.LOADING : FormStatus.INITIAL;
    this.loadingIcon = loading
      ? {
          name: SvgLibraryIcon.TRANSFER_LOADER,
          position: IconPosition.RIGHT,
        }
      : null;
  }

  private addControlListeners(): void {
    this.subscriptions.add(
      this.pnrForm.get('bookingRef').valueChanges.subscribe((val) => {
        const eTicket = this.pnrForm.get('eTicket');
        if (val && eTicket.enabled) {
          eTicket.disable();
        }
        if (!val && eTicket.disabled) {
          eTicket.enable();
        }
      })
    );
    this.subscriptions.add(
      this.pnrForm.get('eTicket').valueChanges.subscribe((val) => {
        const bookingRef = this.pnrForm.get('bookingRef');
        if (val && bookingRef.enabled) {
          bookingRef.disable();
        }
        if (!val && bookingRef.disabled) {
          bookingRef.enable();
        }
      })
    );
  }
}
